import React from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { DifficultyData } from '../types/analytics.types';
import './DifficultyDistribution.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

interface DifficultyDistributionProps {
  data: DifficultyData;
  loading?: boolean;
}

export const DifficultyDistribution: React.FC<DifficultyDistributionProps> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="difficulty-distribution">
        <h4>Difficulty Analysis</h4>
        <div className="loading-skeleton" style={{ height: '350px' }}></div>
      </div>
    );
  }

  if (!data || !data.by_difficulty || data.by_difficulty.length === 0) {
    return (
      <div className="difficulty-distribution">
        <h4>Difficulty Analysis</h4>
        <p className="no-data">No difficulty data available</p>
      </div>
    );
  }

  // Sort by difficulty level
  const sortedData = [...data.by_difficulty].sort((a, b) => a.difficulty - b.difficulty);

  // Success rate chart data
  const successLabels = sortedData.map(d => `Level ${d.difficulty}`);
  const successRates = sortedData.map(d => d.success_rate);

  const successChartData = {
    labels: successLabels,
    datasets: [
      {
        label: 'Success Rate (%)',
        data: successRates,
        backgroundColor: successRates.map(rate => {
          if (rate >= 80) return 'rgba(75, 192, 192, 0.8)';
          if (rate >= 60) return 'rgba(255, 206, 86, 0.8)';
          return 'rgba(255, 99, 132, 0.8)';
        }),
        borderColor: successRates.map(rate => {
          if (rate >= 80) return 'rgba(75, 192, 192, 1)';
          if (rate >= 60) return 'rgba(255, 206, 86, 1)';
          return 'rgba(255, 99, 132, 1)';
        }),
        borderWidth: 2
      }
    ]
  };

  const successChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        callbacks: {
          label: function(context: any) {
            const index = context.dataIndex;
            const item = sortedData[index];
            return [
              `Success Rate: ${item.success_rate.toFixed(1)}%`,
              `Correct: ${item.correct.toLocaleString()}`,
              `Incorrect: ${item.incorrect.toLocaleString()}`,
              `Total Attempts: ${item.total_attempts.toLocaleString()}`,
              `Avg Response: ${(item.avg_response_time_ms / 1000).toFixed(1)}s`
            ];
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          callback: function(value: any) {
            return value + '%';
          },
          font: {
            size: 11
          }
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 11
          }
        }
      }
    }
  };

  // Distribution pie chart
  const distributionLabels = Object.keys(data.distribution || {}).map(key => `Level ${key}`);
  const distributionValues = Object.values(data.distribution || {});

  const distributionChartData = {
    labels: distributionLabels,
    datasets: [
      {
        data: distributionValues,
        backgroundColor: [
          'rgba(54, 162, 235, 0.8)',
          'rgba(255, 206, 86, 0.8)',
          'rgba(255, 99, 132, 0.8)',
          'rgba(75, 192, 192, 0.8)',
          'rgba(153, 102, 255, 0.8)',
          'rgba(255, 159, 64, 0.8)'
        ],
        borderColor: [
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(255, 99, 132, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)'
        ],
        borderWidth: 2
      }
    ]
  };

  const distributionChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        callbacks: {
          label: function(context: any) {
            const label = context.label || '';
            const value = context.parsed;
            const total = distributionValues.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  // Calculate overall stats
  const totalAttempts = sortedData.reduce((sum, d) => sum + d.total_attempts, 0);
  const avgSuccessRate = sortedData.reduce((sum, d) => sum + d.success_rate, 0) / sortedData.length;

  return (
    <div className="difficulty-distribution">
      <h4>Difficulty Analysis</h4>

      <div className="difficulty-content">
        <div className="chart-section">
          <h5>Success Rate by Difficulty Level</h5>
          <div className="chart-wrapper" style={{ height: '280px' }}>
            <Bar data={successChartData} options={successChartOptions} />
          </div>
        </div>

        <div className="chart-section">
          <h5>Difficulty Distribution</h5>
          <div className="chart-wrapper" style={{ height: '280px' }}>
            <Doughnut data={distributionChartData} options={distributionChartOptions} />
          </div>
        </div>
      </div>

      <div className="difficulty-stats">
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <div className="stat-value">{sortedData.length}</div>
            <div className="stat-label">Difficulty Levels</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🎯</div>
          <div className="stat-content">
            <div className="stat-value">{avgSuccessRate.toFixed(1)}%</div>
            <div className="stat-label">Avg Success Rate</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📈</div>
          <div className="stat-content">
            <div className="stat-value">{totalAttempts.toLocaleString()}</div>
            <div className="stat-label">Total Attempts</div>
          </div>
        </div>
        {data.difficulty_range && (
          <div className="stat-card">
            <div className="stat-icon">🎚️</div>
            <div className="stat-content">
              <div className="stat-value">
                {data.difficulty_range.min} - {data.difficulty_range.max}
              </div>
              <div className="stat-label">Difficulty Range</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DifficultyDistribution;
